import React, { useState } from "react";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";

const sectors = [
  "MSME", "Blue Collar", "SHG", "Families", "Pre-school Mothers",
  "Teenagers", "Teachers", "Students", "Government", "Corporate",
  "Farmers", "HORECA", "Manufacturing"
];

const useCases = {
  MSME: ["Random Customer Reward", "Product Quality Check", "Order Prioritization"],
  "Blue Collar": ["Shift Allocation", "Task Assignment", "Safety Drill"],
  SHG: ["Resource Sharing", "Micro-Grant Lottery", "Event Role Allocation"],
  Families: ["Chore Assignment", "Reward Games", "Activity Rotation"],
  "Pre-school Mothers": ["Daily Chore", "Fun Reward", "Learning Exercise"],
  Teenagers: ["Random Activity", "Quiz Selection", "Task Assignment"],
  Teachers: ["Student Selection", "Group Formation", "Quiz Questions"],
  Students: ["Random Presentation", "Team Formation", "Classroom Duty"],
  Government: ["Beneficiary Selection", "Audit Scheduling", "Lottery Allocation"],
  Corporate: ["Prize Draw", "Task Rotation", "Survey Selection"],
  Farmers: ["Plot Sampling", "Seed Trial", "Subsidy Lottery"],
  HORECA: ["Table Assignment", "Lucky Draw", "Staff Duty Rotation"],
  Manufacturing: ["Product Inspection", "Machine Maintenance", "Shift Bonus"]
};

const RandomLifeHome = () => {
  const [activeSector, setActiveSector] = useState(sectors[0]);
  const [selectedUseCase, setSelectedUseCase] = useState("");
  const [title, setTitle] = useState("");
  const [jsonData, setJsonData] = useState([]);
  const [n, setN] = useState(1);
  const [output, setOutput] = useState([]);

  // Handle JSON file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = JSON.parse(evt.target.result);
        if (Array.isArray(data) && data.length <= 10000) {
          setJsonData(data);
        } else {
          alert("JSON must be an array with max 10,000 records.");
        }
      } catch (err) {
        alert("Invalid JSON file.");
      }
    };
    reader.readAsText(file);
  };

  // Generate random output
  const generateRandom = () => {
    if (!jsonData.length) {
      alert("Upload a JSON file first.");
      return;
    }
    if (n <= 0) {
      alert("Enter a valid number of outputs.");
      return;
    }
    const results = [];
    for (let i = 0; i < n; i++) {
      const index = Math.floor(Math.random() * jsonData.length);
      results.push(jsonData[index]);
    }
    setOutput(results);
  };

  // Export JSON
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(output, null, 2)], { type: "application/json" });
    saveAs(blob, "random_output.json");
  };

  // Export PDF
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(30, 30, 30);
    doc.rect(0, 0, 210, 297, "F"); // dark background
    doc.setTextColor(255, 255, 255);
    doc.text(title || "Random Output", 10, 20);
    output.forEach((item, idx) => {
      doc.text(`${idx + 1}. ${JSON.stringify(item)}`, 10, 30 + idx * 10);
    });
    doc.save("random_output.pdf");
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      <h1 className="text-lg lg:text-xl font-bold mb-4">🎲 Random Life Utility</h1>

      {/* Sector Tabs */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 place-items-center border-t border-b border-blue-600 mb-4 py-4">
        {sectors.map((sector) => (
          <button
            key={sector}
            onClick={() => { setActiveSector(sector); setSelectedUseCase(""); }}
            className={`text-xs lg:text-sm px-3 py-1 rounded-md ${
              activeSector === sector ? "bg-blue-500" : "bg-gray-700"
            }`}
          >
            {sector}
          </button>
        ))}
      </div>

      {/* Use Cases Grid */}
      <div className="grid grid-cols-3 lg:grid-cols-3 gap-2 mb-2 place-items-center border-b border-cyan-400 py-4">
        {useCases[activeSector].map((uc) => (
          <button
            key={uc}
            onClick={() => setSelectedUseCase(uc)}
            className={`px-3 py-2 rounded-lg border ${
              selectedUseCase === uc ? "border-blue-500" : "border-gray-600"
            } hover:border-blue-400`}
          >
            {uc}
          </button>
        ))}
      </div>

      {/* Input Section */}
      {selectedUseCase && (
        <div className="mb-4 space-y-2">
          <p>Title:</p>
          <input
            type="text"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full p-2 rounded bg-gray-800 border border-gray-600"
          />
          <p>How Many Numbers To Generate?</p>
          <input
            type="number"
            value={n}
            onChange={(e) => setN(Number(e.target.value))}
            className="w-10 p-2 rounded bg-gray-800 border border-gray-600"
          />
          <input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            className="w-40 p-2 rounded bg-gray-800 border border-gray-600"
          />
          <button
            onClick={generateRandom}
            className="text-xs w-32 lg:text-sm lg:w-64 bg-blue-500 hover:bg-blue-600 py-2 rounded"
          >
            Generate Random Output
          </button>
        </div>
      )}

      {/* Output Section */}
      {output.length > 0 && (
        <div className="mb-4 space-y-2">
          <h2 className="text-xl font-semibold">Generated Output:</h2>
          <div className="max-h-64 overflow-auto bg-gray-800 p-2 rounded">
            {output.map((item, idx) => (
              <div key={idx} className="border-b border-gray-700 py-1">
                {JSON.stringify(item)}
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <button
              onClick={exportJSON}
              className="flex-1 text-sm w-32 bg-green-500 hover:bg-green-600 py-2 rounded"
            >
              Export JSON
            </button>
            <button
              onClick={exportPDF}
              className="flex-1 text-sm w-48 bg-purple-500 hover:bg-purple-600 py-2 rounded"
            >
              Download Output as PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RandomLifeHome;
